/**
 * Helpers for preserving the URL a user was trying to visit when they get
 * bounced to the auth flow.
 *
 * Usage:
 *   - Auth guards that send users to `/login` (or `/login-pwd`) call
 *     `buildLoginUrl(loginPath, location.pathname + location.search)` so the
 *     intended target is captured in `?redirect=...`.
 *   - The login pages call `readRedirectParam(searchParams)` after a
 *     successful auth and, if a safe target is present, navigate there
 *     instead of the tenant home.
 *
 * Threat model: `redirect` is attacker-controllable (anyone can craft
 * `https://app/.../login?redirect=...` and email it). We therefore only ever
 * navigate to **same-origin, relative** paths, never to an absolute URL,
 * protocol-relative URL, or back into an auth surface (which would loop).
 */

const AUTH_SURFACE_PATHS = [
  '/login',
  '/login-pwd',
  '/register',
  '/accept-invite',
  '/onboarding',
];

export function isSafeRedirectPath(target: string | null | undefined): boolean {
  if (!target) return false;
  // Must be a relative path starting with a single '/'.
  if (!target.startsWith('/')) return false;
  // `//host/...` is a protocol-relative URL → off-origin.
  if (target.startsWith('//')) return false;
  // Some browsers normalise '\' to '/'. Block to be safe.
  if (target.startsWith('/\\')) return false;
  // Avoid bouncing back into auth surfaces (would loop).
  for (const p of AUTH_SURFACE_PATHS) {
    if (target === p || target.startsWith(`${p}/`) || target.startsWith(`${p}?`)) {
      return false;
    }
  }
  return true;
}

/**
 * Build a login URL that carries the user's intended destination as a
 * `?redirect=...` query param. Falls back to the bare login path if the
 * target is not a safe relative path.
 */
export function buildLoginUrl(loginPath: string, currentPathWithSearch: string): string {
  if (!isSafeRedirectPath(currentPathWithSearch)) return loginPath;
  const sep = loginPath.includes('?') ? '&' : '?';
  return `${loginPath}${sep}redirect=${encodeURIComponent(currentPathWithSearch)}`;
}

/** Read & validate the `redirect` query param. Returns null if missing or unsafe. */
export function readRedirectParam(
  search: string | URLSearchParams,
): string | null {
  const sp = typeof search === 'string' ? new URLSearchParams(search) : search;
  const target = sp.get('redirect');
  return isSafeRedirectPath(target) ? target : null;
}
